import { MenuItem, Text } from "@chakra-ui/react";
import { Glossary } from "src/components/Text/Glossary";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { maybePluralize } from "src/services/format";
import * as GQL from "src/types/graphql";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canAddToWaitlists } from "../validations/addToWaitlists";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkWaitlist } from "./useEnqueueBulkWaitlist";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  menuLabel?: string;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "You will add the selected form to its school and grade waitlist. You can remove it anytime.",
    "You will add the selected forms to each school and grade waitlist. You can remove them anytime."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, add to waitlist", "Yes, add to waitlists");

export const BulkAddToWaitlists = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  menuLabel = "Add to waitlists",
}: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkWaitlist = useEnqueueBulkWaitlist();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "ADD_TO_WAITLIST",
      bulk: true,
      count,
      async: true,
    });

    if (
      !(await confirm({
        header: (
          <Text>{`Add ${count} ${maybePluralize(
            count,
            "form"
          )} to ${maybePluralize(count, "waitlist")}?`}</Text>
        ),
        body: (
          <Text>
            <Glossary>{confirmationBodyText(count)}</Glossary>
          </Text>
        ),
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      }))
    ) {
      trackFormAction({
        verb: "Canceled",
        actionType: "ADD_TO_WAITLIST",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "ADD_TO_WAITLIST",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkWaitlist({ action: "ADD_TO_WAITLIST" });
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canAddToWaitlists(
            selectedFormSchoolStatuses,
            selectedFormSchools,
            (subStatus) =>
              isSubStatusTransitionAllowed(
                subStatus,
                GQL.waitlist_status_enum.Waitlisted
              )
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
